// Asana import (/admin/asana-import): Michael's Asana board into Spark, one project at a time.
// Upload the export, map each Asana value once, adjust any line, import it. Nothing is written
// until a line's Import is pressed. The mapping and per-line changes stay in this browser.
// Behaviour: web/docs/features/spark.md, "Importing from Asana".

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Chip, ChipGroup } from '../components/ui/Chip';
import { DropZone } from '../components/ui/DropZone';
import { EmptyState } from '../components/ui/EmptyState';
import FadeIn from '../components/ui/FadeIn';
import { ListStack } from '../components/ui/ListRow';
import PageLayout from '../components/ui/PageLayout';
import { Panel } from '../components/ui/Panel';
import Progress from '../components/ui/Progress';
import Reveal from '../components/ui/Reveal';
import { SearchField } from '../components/ui/SearchField';
import SegTrack from '../components/ui/SegTrack';
import Tooltip from '../components/ui/Tooltip';
import { importAsanaLine, listPeople, previewAsanaImport } from '../Spark/sparkApi';
import type { AsanaLine, AsanaPreview, Person } from '../Spark/sparkApi';
import { errorCode, errorMessage } from '../Spark/sparkUi';
import {
  NO_SECTION, draftIsEmpty, effectiveMapping, eligibility, emptyMapping, forgetDraft, lineBody, lineDraftIsEmpty,
  lineState, loadDraft, pickPeople, planTasks, resolveLine, saveDraft,
} from './asanaImportModel';
import type { EffectiveMapping, Eligible } from './asanaImportModel';
import AsanaImportMapping from './AsanaImportMapping';
import { LineRow, LinesHead } from './AsanaImportLine';
import type { ImportedAs, LineDraft, LineState, MappingChoices, ResolvedLine } from './asanaImportTypes';

const NO_LINES: AsanaLine[] = [];
type StateFilter = 'all' | LineState;

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** "257 projects in this export. 1 from before 2026 is hidden. 118 subtasks could not be placed under a project." */
export const fileSummary = (f: AsanaPreview['file']): string => [
  `${f.lines} ${plural(f.lines, 'project', 'projects')} in this export.`,
  f.hidden_before_2026 > 0 ? `${f.hidden_before_2026} from before 2026 ${plural(f.hidden_before_2026, 'is', 'are')} hidden.` : '',
  f.orphan_subtasks > 0
    ? `${f.orphan_subtasks} ${plural(f.orphan_subtasks, 'subtask', 'subtasks')} could not be placed under a project.`
    : '',
].filter(Boolean).join(' ');

const omit = <T,>(rec: Record<string, T>, key: string): Record<string, T> => {
  if (!(key in rec)) return rec;
  const next = { ...rec };
  delete next[key];
  return next;
};

const help = (
  <>
    <p>
      Export the Studio&apos;s board from Asana as .xlsx or .csv and drop it here. Each top-level Asana task becomes one
      line. Nothing goes into Spark until you press Import on a line, and a line already in Spark shows as Imported and
      cannot be imported twice.
    </p>
    <p>
      Map once: each distinct Asana value (pipeline, status, spice, speciality, person) gets its Spark value in the
      mapping, and every line carrying it follows. Change a field on one line to override it there; Use the mapping
      puts it back.
    </p>
    <p>
      A blank in Asana stays blank unless the mapping or you fill it. Name, channel, speciality, pipeline and spice must be
      set before a line can import; everything else may stay empty.
    </p>
    <p>
      Subtasks go in as deliverables, subtasks, or not at all. Spark nests one level, so a subtask with no kept
      deliverable above it goes in as a deliverable. A subtask completed in Asana arrives Ready to Fly.
    </p>
    <p>
      The mapping and your per-line changes are kept in this browser, so you can stop and come back: upload the export
      again to carry on.
    </p>
  </>
);

interface Latest {
  preview: AsanaPreview | null;
  eff: EffectiveMapping | null;
  drafts: Record<string, LineDraft>;
  resolved: Map<string, ResolvedLine>;
}

const AsanaImport: React.FC = () => {
  const [saved] = useState(loadDraft);
  const [mapping, setMapping] = useState<MappingChoices>(() => saved?.mapping ?? emptyMapping());
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>(() => saved?.lines ?? {});
  const [hadSaved, setHadSaved] = useState(() => !draftIsEmpty(saved));

  const [file, setFile] = useState<File | null>(null);
  const [reading, setReading] = useState<File | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AsanaPreview | null>(null);

  const [sparkPeople, setSparkPeople] = useState<Person[] | null>(null);
  const [peopleError, setPeopleError] = useState<string | null>(null);
  const [peopleTry, setPeopleTry] = useState(0);

  const [imported, setImported] = useState<Record<string, ImportedAs>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [mappingOpen, setMappingOpen] = useState(true);
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [sections, setSections] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let live = true;
    setPeopleError(null);
    listPeople()
      .then((r) => { if (live) setSparkPeople(r.people || []); })
      .catch((err) => { if (live) setPeopleError(errorMessage(err, 'Could not load the people in Spark.')); });
    return () => { live = false; };
  }, [peopleTry]);

  // Saved a beat after the last change, and once more on the way out so the last keystroke stays.
  const toSave = useRef({ mapping, drafts });
  toSave.current = { mapping, drafts };
  useEffect(() => {
    const t = window.setTimeout(() => saveDraft({ version: 1, mapping, lines: drafts }), 250);
    return () => window.clearTimeout(t);
  }, [mapping, drafts]);
  useEffect(() => () => saveDraft({ version: 1, mapping: toSave.current.mapping, lines: toSave.current.drafts }), []);

  const read = async (f: File) => {
    setReading(f);
    setReadError(null);
    try {
      const p = await previewAsanaImport(f);
      setPreview(p);
      setFile(f);
      setImported({});
      setErrors({});
      setBusy({});
      setExpanded(new Set());
      setSections(new Set());
      setHadSaved(false);
    } catch (err) {
      setReadError(errorMessage(err, 'This file could not be read. Export it from Asana again, as .xlsx or .csv.'));
    } finally {
      setReading(null);
    }
  };

  const clearFile = () => {
    if (reading) return;
    setFile(null);
    setPreview(null);
    setReadError(null);
  };

  const lines = preview?.lines ?? NO_LINES;
  const eff = useMemo(() => (preview ? effectiveMapping(preview, mapping) : null), [preview, mapping]);
  const people = useMemo(() => pickPeople(sparkPeople, preview), [sparkPeople, preview]);
  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const eligible: Eligible = useMemo(() => eligibility(peopleById, sparkPeople !== null), [peopleById, sparkPeople]);

  // Each line keeps its resolved object until its own draft or the mapping moves, so a keystroke
  // on one line redraws that line, not all of them.
  const cache = useRef(new Map<string, { draft: LineDraft | undefined; eff: EffectiveMapping; eligible: Eligible; r: ResolvedLine }>());
  const resolved = useMemo(() => {
    const out = new Map<string, ResolvedLine>();
    if (!eff) return out;
    for (const l of lines) {
      const d = drafts[l.gid];
      const hit = cache.current.get(l.gid);
      if (hit && hit.draft === d && hit.eff === eff && hit.eligible === eligible) {
        out.set(l.gid, hit.r);
      } else {
        const r = resolveLine(l, d, eff, eligible);
        cache.current.set(l.gid, { draft: d, eff, eligible, r });
        out.set(l.gid, r);
      }
    }
    return out;
  }, [lines, drafts, eff, eligible]);

  // Made once per export, so a row already in Spark keeps the same object between renders.
  const importedBefore = useMemo(() => new Map(lines.filter((l) => l.imported).map((l) => [
    l.gid, { id: l.imported.id, name: l.imported.name, fresh: false } as ImportedAs,
  ])), [lines]);
  const importedOf = useCallback(
    (l: AsanaLine): ImportedAs | null => imported[l.gid] ?? importedBefore.get(l.gid) ?? null,
    [imported, importedBefore],
  );

  const states = useMemo(() => {
    const out = new Map<string, LineState>();
    for (const l of lines) {
      const r = resolved.get(l.gid);
      if (r) out.set(l.gid, lineState(r, !!importedOf(l)));
    }
    return out;
  }, [lines, resolved, importedOf]);

  const counts = useMemo(() => {
    const c = { needs: 0, ready: 0, imported: 0 };
    states.forEach((s) => { c[s] += 1; });
    return c;
  }, [states]);

  // An open line stays on screen whatever the filters say, so finishing its last pick never
  // whisks it away mid-work.
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return lines.filter((l) => {
      if (expanded.has(l.gid)) return true;
      if (stateFilter !== 'all' && states.get(l.gid) !== stateFilter) return false;
      if (sections.size > 0 && !sections.has(l.section || NO_SECTION)) return false;
      if (needle) {
        const own = resolved.get(l.gid)?.name.value || '';
        if (!l.name.toLowerCase().includes(needle) && !own.toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  }, [lines, expanded, stateFilter, states, sections, q, resolved]);

  const latest = useRef<Latest>({ preview, eff, drafts, resolved });
  latest.current = { preview, eff, drafts, resolved };

  const onDraft = useCallback((gid: string, change: (d: LineDraft) => LineDraft) => {
    setDrafts((prev) => {
      const next = change(prev[gid] || {});
      return lineDraftIsEmpty(next) ? omit(prev, gid) : { ...prev, [gid]: next };
    });
  }, []);

  const onToggle = useCallback((gid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  }, []);

  const onDismissError = useCallback((gid: string) => setErrors((e) => omit(e, gid)), []);

  // Held outside state: two presses in one frame would otherwise both see the line as idle.
  const inFlight = useRef(new Set<string>());
  const onImport = useCallback(async (gid: string) => {
    const now = latest.current;
    const line = now.preview?.lines.find((l) => l.gid === gid);
    const r = now.resolved.get(gid);
    if (!line || !r || !now.eff || inFlight.current.has(gid)) return;
    if (!r.link_to && r.missing.length) return;
    inFlight.current.add(gid);
    const body = lineBody(line, r, planTasks(line, now.drafts[gid], now.eff), now.eff);
    const name = 'link_to' in body
      ? line.same_name.find((p) => p.id === body.link_to)?.name || line.name
      : body.project.name;
    setBusy((b) => ({ ...b, [gid]: true }));
    setErrors((e) => omit(e, gid));
    try {
      const res = await importAsanaLine(body);
      setImported((m) => ({ ...m, [gid]: { id: res.id, name, fresh: true } }));
      // In Spark now, so its picks have nothing left to shape.
      setDrafts((prev) => omit(prev, gid));
    } catch (err) {
      if (errorCode(err) === 'already_imported') {
        setImported((m) => ({ ...m, [gid]: { id: null, name: line.name, fresh: true } }));
      } else {
        setErrors((e) => ({ ...e, [gid]: errorMessage(err, 'Could not import this project. Try again.') }));
      }
    } finally {
      inFlight.current.delete(gid);
      setBusy((b) => omit(b, gid));
    }
  }, []);

  const onMapping = useCallback((change: (m: MappingChoices) => MappingChoices) => setMapping(change), []);

  const forget = () => {
    setMapping(emptyMapping());
    setDrafts({});
    setHadSaved(false);
    forgetDraft();
  };

  const toggleSection = (value: string) => setSections((prev) => {
    const next = new Set(prev);
    if (next.has(value)) next.delete(value); else next.add(value);
    return next;
  });
  const filtering = q.trim() !== '' || stateFilter !== 'all' || sections.size > 0;
  const clearFilters = () => { setQ(''); setStateFilter('all'); setSections(new Set()); };

  const savedCount = Object.keys(drafts).length;

  return (
    <PageLayout
      kind="workspace"
      width="wide"
      className="space-y-5 pt-4"
      header={(
        <PageHeader
          title="Asana import"
          back={{ to: '/admin', label: 'Admin' }}
          lede="Bring the Studio's Asana board into Spark one project at a time; nothing is written until you import a line."
          help={help}
          tools={[{
            label: 'Forget saved picks',
            hint: 'The mapping and every per-project change kept in this browser',
            tone: 'danger',
            disabled: draftIsEmpty({ version: 1, mapping, lines: drafts }),
            confirm: { question: 'Forget the mapping and every per-project change?', yes: 'Forget them' },
            onSelect: forget,
          }]}
        />
      )}
    >
      <Panel title="The Asana export">
        <div className="grid gap-x-6 gap-y-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <DropZone
            what="the Asana export"
            accept=".xlsx,.csv"
            hint="The .xlsx or .csv file Asana exports, up to 20 MB."
            chosen={reading || file}
            onFiles={(files) => { if (files[0]) read(files[0]); }}
            onClear={file && !reading ? clearFile : undefined}
            busy={reading ? 'Reading the export' : null}
            error={readError}
          />
          <div className="flex min-w-0 flex-col justify-center">
            {preview ? (
              <FadeIn key="read" className="space-y-3">
                <p className="m-0 max-w-[65ch] text-sm leading-5 text-on-surface">{fileSummary(preview.file)}</p>
                <Progress
                  value={counts.imported}
                  max={lines.length}
                  label="Projects in Spark"
                  display="count"
                  unit="in Spark"
                  success={lines.length > 0 && counts.imported === lines.length}
                  className="max-w-md"
                />
              </FadeIn>
            ) : (
              <FadeIn key="waiting">
                <p className="m-0 max-w-[65ch] text-sm leading-5 text-on-surface-variant">
                  {hadSaved
                    ? `Your mapping${savedCount ? ` and changes on ${savedCount} ${plural(savedCount, 'project', 'projects')}` : ''} from last time are kept in this browser. Upload the export again to carry on.`
                    : 'Each top-level Asana task becomes one line to check and import. Reading the file writes nothing.'}
                </p>
              </FadeIn>
            )}
          </div>
        </div>
      </Panel>

      <Alert
        show={!!preview && !!peopleError}
        tone="warning"
        onRetry={() => setPeopleTry((n) => n + 1)}
        retryTip="Load the people in Spark again"
      >
        {peopleError ? `${peopleError} Only people matched from the export can be picked, and team slots are not checked against Spark.` : ''}
      </Alert>

      <Reveal show={!!preview && !!eff}>
        {preview && eff && (
          <div className="space-y-5">
            <AsanaImportMapping
              preview={preview}
              eff={eff}
              people={people}
              onMapping={onMapping}
              open={mappingOpen}
              onToggle={() => setMappingOpen((o) => !o)}
            />

            <Panel title="Projects" padding="sm">
              <div role="group" aria-label="Filter projects" className="flex flex-wrap items-center gap-2 pb-2">
                <SearchField
                  size="sm"
                  label="Search projects by name"
                  placeholder="Search by name"
                  value={q}
                  onChange={setQ}
                  className="w-full sm:w-64"
                />
                <SegTrack
                  size="sm"
                  label="State"
                  value={stateFilter}
                  onChange={(k) => setStateFilter(k as StateFilter)}
                  options={[
                    { key: 'all', label: 'All', count: lines.length },
                    { key: 'needs', label: 'Needs a pick', count: counts.needs },
                    { key: 'ready', label: 'Ready', count: counts.ready },
                    { key: 'imported', label: 'Imported', count: counts.imported },
                  ]}
                />
                <span role="status" className="ml-auto text-xs tabular-nums text-on-surface-variant">
                  Showing {visible.length} of {lines.length}
                </span>
              </div>
              {preview.values.sections.length > 1 && (
                <ChipGroup label="Section" className="pb-3">
                  {preview.values.sections.map((s) => (
                    <Chip key={s.value} pressed={sections.has(s.value)} count={s.count} onClick={() => toggleSection(s.value)}>
                      {s.value === NO_SECTION ? 'No section' : s.value}
                    </Chip>
                  ))}
                </ChipGroup>
              )}
              {lines.length === 0 ? (
                <EmptyState variant="area" title="No projects to import">
                  Every top-level task in this export is from before 2026, or the export holds none. Export the board
                  again and check it covers this year.
                </EmptyState>
              ) : (
                <ListStack
                  items={visible}
                  getKey={(l) => l.gid}
                  on="panel"
                  spacing="loose"
                  label="Projects in the export"
                  head={<LinesHead />}
                  empty={(
                    <EmptyState
                      variant="line"
                      action={filtering ? (
                        <Tooltip label="Show every project again">
                          <Button variant="tonal" size="sm" onClick={clearFilters}>Clear filters</Button>
                        </Tooltip>
                      ) : undefined}
                    >
                      No project matches these filters.
                    </EmptyState>
                  )}
                  renderRow={(l) => (
                    <LineRow
                      key={l.gid}
                      line={l}
                      r={resolved.get(l.gid) as ResolvedLine}
                      state={states.get(l.gid) || 'needs'}
                      draft={drafts[l.gid]}
                      eff={eff}
                      people={people}
                      peopleById={peopleById}
                      importedAs={importedOf(l)}
                      busy={!!busy[l.gid]}
                      error={errors[l.gid] || null}
                      expanded={expanded.has(l.gid)}
                      onToggle={onToggle}
                      onDraft={onDraft}
                      onImport={onImport}
                      onDismissError={onDismissError}
                    />
                  )}
                />
              )}
            </Panel>
          </div>
        )}
      </Reveal>
    </PageLayout>
  );
};

export default AsanaImport;
